import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadCounts, useSocket, incrementRoomUnread } from '../../hooks/useSocket';
import InstallPrompt from '../InstallPrompt';
import { useState, useEffect } from 'react';
import { 
  Home, 
  Calendar,
  CalendarOff,
  MessageCircle,
  Users,
  LogOut,
  User
} from 'lucide-react';

function MobileNavItem({ to, icon: Icon, children, badge }) {
  return (
    <NavLink 
      to={to} 
      className={({ isActive }) => 
        isActive ? 'mobile-nav-item-active' : 'mobile-nav-item'
      }
    >
      <div className="relative">
        <Icon className="w-6 h-6" />
        {badge > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      <span>{children}</span>
    </NavLink>
  );
}

export default function ContractorLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { total: unreadCount } = useUnreadCounts();
  const { onRoomUpdated, onRoomCreated } = useSocket();

  // Listen for room updates to increment unread and play sound
  useEffect(() => {
    const unsubscribe = onRoomUpdated(({ roomId }) => {
      // Only increment if not currently on chat page viewing this room
      const isOnChatPage = window.location.pathname.includes('/chat');
      if (!isOnChatPage) {
        incrementRoomUnread(roomId);
      }
    });
    return unsubscribe;
  }, [onRoomUpdated]);

  // Listen for new rooms created (e.g., admin initiating chat)
  useEffect(() => {
    const unsubscribe = onRoomCreated(({ room }) => {
      // New room created - increment unread to notify user
      const isOnChatPage = window.location.pathname.includes('/chat');
      if (!isOnChatPage && room?.id) {
        incrementRoomUnread(room.id);
      }
    });
    return unsubscribe;
  }, [onRoomCreated]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with safe area padding for iOS */}
      <header 
        className="bg-white border-b border-gray-200 sticky top-0 z-40"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center justify-between h-14 px-4">
          <img src="/logo.png" alt="HUBTractor" className="h-9" />
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{user?.name}</span>
            <button 
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-gray-600"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="px-4 py-4">
        <Outlet />
      </main>

      {/* Mobile bottom navigation with safe area padding */}
      <nav 
        className="mobile-nav"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex justify-around">
          <MobileNavItem to="/my/dashboard" icon={Home}>
            Home
          </MobileNavItem>
          <MobileNavItem to="/my/calendar" icon={Calendar}>
            Gigs
          </MobileNavItem>
          <MobileNavItem to="/my/chat" icon={MessageCircle} badge={unreadCount}>
            Chat
          </MobileNavItem>
          <MobileNavItem to="/my/directory" icon={Users}>
            Team
          </MobileNavItem>
          <MobileNavItem to="/my/availability" icon={CalendarOff}>
            Off Days
          </MobileNavItem>
          <MobileNavItem to="/my/profile" icon={User}>
            Profile
          </MobileNavItem>
        </div>
      </nav>

      {/* PWA Install Prompt */}
      <InstallPrompt />
    </div>
  );
}
